import { dailyHoroscope, ZODIAC_SIGNS, type ZodiacSign, zodiacDates } from '@spin/shared';
import { useState } from 'react';
import { Dialog } from '../components/Dialog';
import { useT } from '../i18n';
import { useLocale } from '../store/session';

const STORE_KEY = 'spin.zodiac';

function savedSign(): ZodiacSign | null {
  try {
    const v = localStorage.getItem(STORE_KEY);
    return ZODIAC_SIGNS.includes(v as ZodiacSign) ? (v as ZodiacSign) : null;
  } catch {
    return null;
  }
}

/** The local calendar date as YYYY-MM-DD: the reading changes at the player's midnight. */
function today(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function SignPic({ sign }: { sign: ZodiacSign }) {
  return <img className="zodiac-pic" src={`/img/zodiac/${sign}.webp`} alt="" draggable={false} />;
}

/** Daily horoscope: pick a sign once (remembered on this device), then read today's reading. */
export function HoroscopeDialog() {
  const t = useT();
  const locale = useLocale();
  const [sign, setSign] = useState<ZodiacSign | null>(savedSign);
  const [picking, setPicking] = useState(sign === null);

  const choose = (s: ZodiacSign) => {
    setSign(s);
    setPicking(false);
    try {
      localStorage.setItem(STORE_KEY, s);
    } catch {
      // Private mode: the choice lasts until the dialog closes.
    }
  };

  const dateLabel = (month: number, day: number) =>
    new Date(2001, month - 1, day).toLocaleDateString(locale, { day: 'numeric', month: 'short' });

  if (picking || !sign) {
    return (
      <Dialog className="horoscope-dialog" title={t('horoscope.pick')} testId="horoscope">
        <div className="zodiac-grid">
          {ZODIAC_SIGNS.map((s) => {
            const [from, to] = zodiacDates(s);
            return (
              <button
                key={s}
                className={s === sign ? 'zodiac-cell on' : 'zodiac-cell'}
                onClick={() => choose(s)}
                data-testid={`zodiac-${s}`}
              >
                <SignPic sign={s} />
                <b>{t(`horoscope.sign.${s}`)}</b>
                <small>
                  {dateLabel(...from)} – {dateLabel(...to)}
                </small>
              </button>
            );
          })}
        </div>
      </Dialog>
    );
  }

  const r = dailyHoroscope(sign, today());
  const date = new Date().toLocaleDateString(locale, { day: 'numeric', month: 'long' });
  return (
    <Dialog className="horoscope-dialog" title={t('horoscope.title')} testId="horoscope">
      <div className="horoscope-head">
        <SignPic sign={sign} />
        <div>
          <b>{t(`horoscope.sign.${sign}`)}</b>
          <small>{t('horoscope.today', { date })}</small>
        </div>
      </div>
      <p className="horoscope-text" data-testid="horoscope-text">
        {t(`horoscope.general.${r.general}`)}
      </p>
      <div className="horoscope-love">
        <b>❤ {t('horoscope.love_title')}</b>
        <p>{t(`horoscope.love.${r.love}`)}</p>
      </div>
      <div className="horoscope-lucky">
        <span>
          {t('horoscope.lucky_number')}: <b>{r.luckyNumber}</b>
        </span>
        <span>
          {t('horoscope.lucky_color')}: <b>{t(`horoscope.color.${r.color}`)}</b>
        </span>
      </div>
      <button className="btn btn-block horoscope-change" onClick={() => setPicking(true)}>
        {t('horoscope.change')}
      </button>
    </Dialog>
  );
}
